import { createStore } from 'vuex'
import axios from 'axios'


const WP_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : `${process.env.VUE_APP_API_BASE ?? 'http://localhost:8888'}/wp-json/wp/v2`

const CF7_BASE = process.env.NODE_ENV === 'production'
  ? '/api-cf7'
  : `${process.env.VUE_APP_API_BASE ?? 'http://localhost:8888'}/wp-json/contact-form-7/v1`

const wp = axios.create({
  baseURL: WP_BASE,
  timeout: 10000,
  auth: process.env.NODE_ENV !== 'production' && process.env.VUE_APP_API_USER
    ? { username: process.env.VUE_APP_API_USER, password: process.env.VUE_APP_API_PASS ?? '' }
    : undefined,
})

const cf7 = axios.create({
  baseURL: CF7_BASE,
  timeout: 10000,
})

function decodeHtml(str) {
  if (!str) return ''
  return str.replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(n))
}

// 通用快取 fetch：同一 endpoint 只打一次，進行中的請求也會被複用
const _apiCache = new Map()
function cachedGet(path) {
  if (_apiCache.has(path)) return Promise.resolve(_apiCache.get(path)).then(v => v)
  const promise = wp.get(path)
    .then(({ data }) => { _apiCache.set(path, data); return data })
    .catch(e => { _apiCache.delete(path); throw e })
  _apiCache.set(path, promise)
  return promise
}

async function fetchMediaUrl(id) {
  if (!id) return ''
  const data = await cachedGet(`/media/${id}`)
  return data.source_url ?? ''
}

function acfExcerpt(str) {
  return str ? str.replace(/\r\n|\r|\n/g, '<br>') : null
}

async function resolveCol(col) {
  if (!col || col.type === 'none') return col
  if (col.type === 'image' && col.image) {
    return { ...col, imageUrl: await fetchMediaUrl(col.image) }
  }
  if (col.type === 'slide' && Array.isArray(col.slide) && col.slide.length) {
    const slideUrls = await Promise.all(col.slide.map(id => fetchMediaUrl(id)))
    return { ...col, slideUrls }
  }
  return col
}

async function parseBlocks(blocks) {
  if (!Array.isArray(blocks)) return []
  const result = []
  for (const block of blocks) {
    if (block.acf_fc_layout === 'two_column') {
      const [left, right] = await Promise.all([
        resolveCol(block.left ?? {}),
        resolveCol(block.right ?? {}),
      ])
      result.push({ layout: 'two_column', left, right })
    } else if (block.acf_fc_layout === 'three_column') {
      const [left, center, right] = await Promise.all([
        resolveCol(block.left ?? {}),
        resolveCol(block.center ?? {}),
        resolveCol(block.right ?? {}),
      ])
      result.push({ layout: 'three_column', left, center, right })
    } else if (block.acf_fc_layout === 'break') {
      result.push({ layout: 'break' })
    } else {
      result.push({ layout: 'one_column', ...await resolveCol(block) })
    }
  }
  return result
}

async function parseContentBlocks(contentBlock, directContent) {
  // 優先使用新的頂層 content 欄位，fallback 到舊的 content_block repeater
  if (Array.isArray(directContent) && directContent.length) {
    return parseBlocks(directContent)
  }
  if (!Array.isArray(contentBlock)) return []
  const result = []
  for (const section of contentBlock) {
    const blocks = section.content
    if (!Array.isArray(blocks)) continue
    result.push(...await parseBlocks(blocks))
  }
  return result
}

function mapTermIds(ids = [], termCache = []) {
  return ids.map(id => termCache.find(t => t.id === id)).filter(Boolean)
}

function formatPeriod(time) {
  if (!time) return ''
  const fmt = (s) => {
    if (!s) return ''
    return `${s.slice(0, 4)}.${s.slice(4, 6)}`
  }
  const start = fmt(time.start_time)
  const end   = time.now ? '現在' : fmt(time.end_time)
  return end ? `${start} — ${end}` : start
}

function mapExperiences(list = [], skillMap = {}) {
  return list.map((item, i) => ({
    id:          i + 1,
    title:       item.title    ?? '',
    company:     item.location ?? '',
    period:      formatPeriod(item.time),
    description: item.context  ?? '',
    tags:        (item.skills ?? []).map(id => skillMap[id]).filter(Boolean),
    pay:         item.pay?.show ? { monthly: item.pay.monthly ?? 0, annual: item.pay.annual ?? 0 } : null,
  }))
}

function mapEducation(list = []) {
  return list.map((item, i) => ({
    id:          i + 1,
    department:  item.title    ?? '',
    school:      item.location ?? '',
    degree:      '',
    period:      formatPeriod(item.time),
    description: item.context  ?? '',
  }))
}

const FALLBACK_EXPERIENCES = { experiences: [], education: [] }

const FALLBACK_SKILLS = { skills: [], certificates: [] }

const FALLBACK_ABOUT = {
  name: 'Joe Lin',
  nickname: [{ name: '邪神' }, { name: '小刀' }],
  birthday: '19910410',
  occupation: {
    main: 'DevOps Engineer',
    sub: [{ name: '週末刀匠' }, { name: '模型師' }],
  },
  interests: [],
  avatar: { front: '', back: '' },
  story: { about: '(個人介紹)', background: '(背景介紹)', currently: '(最近再進行什麼)' },
  links: [
    { label: 'GitHub',      url: 'https://github.com/ouroboros-prime',  icon: 'fab fa-github' },
    { label: 'LinkedIn',    url: '#',                                    icon: 'fab fa-linkedin' },
    { label: 'Twitter / X', url: '#',                                    icon: 'fab fa-x-twitter' },
    { label: 'Instagram',   url: '#',                                    icon: 'fab fa-instagram' },
    { label: 'Facebook',    url: '#',                                    icon: 'fab fa-facebook-f' },
    { label: 'Email',       url: 'mailto:service@ouroboros.me',           icon: 'fa-solid fa-envelope' },
  ],
}

export default createStore({
  state: {
    currentPage: 'home',
    isFlipping: false,
    splashPlayed: false,
    isLoading: false,
    aboutProfile: null,
    aboutFetched: false,
    experiencesData: null,
    experiencesFetched: false,
    skillsData: null,
    skillsFetched: false,
    certsFetched: false,
    skillIdMap: {},             // { [id]: name }，供 experiences 複用，避免重複打 API
    commissionOpen: true,       // ACF show 欄位，預設開放（fetch 前不鎖定）
    commissionFetched: false,
    blogPosts: [],
    blogFetched: false,
    blogPage: 1,
    blogHasMore: true,
    blogLoading: false,
    blogTerms: {},        // { categories: [...], tags: [...], skills: [...], interests: [...] }
    blogPostCache: {},    // { [slug]: post }
  },
  getters: {
    currentPage:        (state) => state.currentPage,
    isFlipping:         (state) => state.isFlipping,
    aboutProfile:       (state) => state.aboutProfile    ?? FALLBACK_ABOUT,
    experiencesData:    (state) => state.experiencesData ?? FALLBACK_EXPERIENCES,
    skillsData:         (state) => state.skillsData      ?? FALLBACK_SKILLS,
    aboutFetched:       (state) => state.aboutFetched,
    experiencesFetched: (state) => state.experiencesFetched,
    skillsFetched:      (state) => state.skillsFetched,
    certsFetched:       (state) => state.certsFetched,
    commissionOpen:     (state) => state.commissionOpen,
    commissionFetched:  (state) => state.commissionFetched,
    blogPosts:          (state) => state.blogPosts,
    blogFetched:        (state) => state.blogFetched,
    blogHasMore:        (state) => state.blogHasMore,
    blogLoading:        (state) => state.blogLoading,
    blogTerms:          (state) => state.blogTerms,
    blogPostCache:      (state) => state.blogPostCache,
  },
  mutations: {
    SET_CURRENT_PAGE(state, page) {
      state.currentPage = page
    },
    SET_FLIPPING(state, val) {
      state.isFlipping = val
    },
    SET_SPLASH_PLAYED(state) {
      state.splashPlayed = true
      sessionStorage.setItem('splashPlayed', '1')
    },
    SET_LOADING(state, val) {
      state.isLoading = val
    },
    SET_ABOUT_PROFILE(state, profile) {
      state.aboutProfile = profile
      state.aboutFetched = true
    },
    SET_EXPERIENCES(state, data) {
      state.experiencesData    = data
      state.experiencesFetched = true
    },
    SET_SKILLS(state, data) {
      state.skillsData    = data
      state.skillsFetched = true
    },
    SET_CERTS(state, certificates) {
      state.skillsData  = { ...state.skillsData, certificates }
      state.certsFetched = true
    },
    SET_SKILL_ID_MAP(state, map) {
      state.skillIdMap = map
    },
    SET_COMMISSION_SETTINGS(state, open) {
      state.commissionOpen    = open
      state.commissionFetched = true
    },
    APPEND_BLOG_POSTS(state, posts) {
      state.blogPosts = [...state.blogPosts, ...posts]
      state.blogFetched = true
    },
    SET_BLOG_PAGE(state, page) {
      state.blogPage = page
    },
    SET_BLOG_HAS_MORE(state, val) {
      state.blogHasMore = val
    },
    SET_BLOG_LOADING(state, val) {
      state.blogLoading = val
    },
    RESET_BLOG(state) {
      state.blogPosts   = []
      state.blogFetched = false
      state.blogPage    = 1
      state.blogHasMore = true
      state.blogLoading = false
    },
    SET_BLOG_TERMS(state, { taxonomy, terms }) {
      state.blogTerms = { ...state.blogTerms, [taxonomy]: terms }
    },
    SET_BLOG_POST_CACHE(state, post) {
      state.blogPostCache = { ...state.blogPostCache, [post.slug]: post }
    },
  },
  actions: {
    setCurrentPage({ commit }, page) {
      commit('SET_CURRENT_PAGE', page)
    },
    setFlipping({ commit }, val) {
      commit('SET_FLIPPING', val)
    },
    markSplashPlayed({ commit }) {
      commit('SET_SPLASH_PLAYED')
    },
    setLoading({ commit }, val) {
      commit('SET_LOADING', val)
    },
    async fetchAboutProfile({ commit, state }) {
      if (state.aboutFetched) return
      try {
        const { data } = await wp.get('/pages', { params: { slug: 'about' } })
        const acf = data[0]?.acf
        if (!acf) throw new Error('no acf')
        const interestIds = acf.interests ?? []
        const [frontUrl, backUrl, interests] = await Promise.all([
          fetchMediaUrl(acf.avatar?.front),
          fetchMediaUrl(acf.avatar?.back),
          wp.get('/interests', { params: { per_page: 100 } })
            .then(({ data }) => data
              .filter(r => interestIds.includes(r.id))
              .map(r => ({ id: r.id, name: r.name ?? '' }))
            )
            .catch(() => []),
        ])
        commit('SET_ABOUT_PROFILE', {
          name:     acf.name ?? '',
          nickname: acf.nickname ?? [],
          birthday: acf.birthday ?? '',
          occupation: {
            main: acf.occupation?.main ?? '',
            sub:  acf.occupation?.sub  ?? [],
          },
          interests,
          avatar: { front: frontUrl, back: backUrl },
          story: {
            about:      acf.story?.about      ?? '',
            background: acf.story?.background ?? '',
            currently:  acf.story?.currently  ?? '',
          },
          links: acf.links ?? [],
        })
      } catch (e) {
        console.error('[store] fetchAboutProfile 失敗，使用 fallback', e)
        commit('SET_ABOUT_PROFILE', FALLBACK_ABOUT)
      }
    },
    async fetchSkillsData({ commit, state }) {
      if (state.skillsFetched) return
      try {
        const [{ data: allSkills }, { data: allCerts }] = await Promise.all([
          wp.get('/skills', { params: { per_page: 100 } }),
          wp.get('/certificates', { params: { per_page: 100 } }),
        ])

        const topCategories = allSkills.filter(s => s.parent === 0)
        const childSkills   = allSkills.filter(s => s.parent !== 0)

        // skillIdMap 供 fetchExperiences 複用，建好後 resolve 同步點
        const skillIdMap = Object.fromEntries(
          allSkills.map(s => [s.id, decodeHtml(s.name)])
        )
        commit('SET_SKILL_ID_MAP', skillIdMap)

        const skills = topCategories
          .map(cat => ({
            category: decodeHtml(cat.name),
            icon:     cat.acf?.icon ?? 'fa-solid fa-tag',
            items: [
              ...(cat.acf?.show ? [{
                name:       decodeHtml(cat.name),
                icon:       cat.acf?.icon   ?? 'fa-solid fa-circle',
                desc:       cat.description ?? '',
                usageCount: cat.count       ?? 0,
                url:        cat.acf?.url    ?? '',
              }] : []),
              ...childSkills
                .filter(s => s.parent === cat.id && s.acf?.show === true)
                .map(s => ({
                  name:       decodeHtml(s.name),
                  icon:       s.acf?.icon   ?? 'fa-solid fa-circle',
                  desc:       s.description ?? '',
                  usageCount: s.count       ?? 0,
                  url:        s.acf?.url    ?? '',
                })),
            ],
          }))
          .filter(cat => cat.items.length > 0)

        // Skills tab 可以立刻顯示，certificates 先給空陣列
        commit('SET_SKILLS', { skills, certificates: [] })

        // Certificates 的 media fetch 在背景繼續，完成後再更新
        const childToParent = Object.fromEntries(childSkills.map(s => [s.id, s.parent]))
        const topCatMap     = Object.fromEntries(topCategories.map(c => [c.id, c]))

        Promise.all(
          allCerts.map(async cert => {
            const imgUrl = await fetchMediaUrl(cert.featured_media)
            return {
              name:     decodeHtml(cert.title?.rendered ?? ''),
              icon:     'fa-solid fa-award',
              image:    imgUrl,
              url:      cert.acf?.url ?? '',
              skillIds: cert.skills ?? [],
            }
          })
        ).then(certsWithImg => {
          const usedCatIds = new Set(
            certsWithImg.flatMap(c =>
              c.skillIds.map(id => childToParent[id] ?? (topCatMap[id] ? id : null)).filter(Boolean)
            )
          )
          const certificates = [...usedCatIds]
            .map(catId => {
              const cat = topCatMap[catId]
              if (!cat) return null
              return {
                category: cat.name,
                icon:     cat.acf?.icon ?? 'fa-solid fa-tag',
                items: certsWithImg.filter(c =>
                  c.skillIds.some(id => (childToParent[id] ?? id) === catId)
                ),
              }
            })
            .filter(Boolean)
          commit('SET_CERTS', certificates)
        }).catch(e => {
          console.error('[store] fetchCerts 失敗，使用空列表', e)
          commit('SET_CERTS', [])
        })
      } catch (e) {
        console.error('[store] fetchSkillsData 失敗，使用 fallback', e)
        commit('SET_SKILLS', FALLBACK_SKILLS)
        commit('SET_CERTS', [])
      }
    },

    async fetchExperiences({ commit, state }) {
      if (state.experiencesFetched) return
      try {
        const { data } = await wp.get('/pages', { params: { slug: 'experiences' } })
        const acf = data[0]?.acf
        if (!acf) throw new Error('no acf')

        const allSkillIds = [...new Set(
          (acf.experiences ?? []).flatMap(item => item.skills ?? [])
        )]

        // skillIdMap 由 fetchSkillsData 在 main.js 中先行建好，此處直接取用
        const cached = state.skillIdMap
        const missingIds = allSkillIds.filter(id => !(id in cached))
        const fetchedEntries = await Promise.all(
          missingIds.map(id =>
            cachedGet(`/skills/${id}`)
              .then(data => [id, decodeHtml(data.name ?? '')])
              .catch(() => null)
          )
        )
        const skillMap = {
          ...cached,
          ...Object.fromEntries(fetchedEntries.filter(Boolean)),
        }

        commit('SET_EXPERIENCES', {
          experiences: mapExperiences(acf.experiences, skillMap),
          education:   mapEducation(acf.education),
        })
      } catch (e) {
        console.error('[store] fetchExperiences 失敗，使用 fallback', e)
        commit('SET_EXPERIENCES', FALLBACK_EXPERIENCES)
      }
    },

    async fetchCommissionSettings({ commit, state }) {
      if (state.commissionFetched) return
      try {
        const { data } = await wp.get('/pages', { params: { slug: 'commission' } })
        const show = data[0]?.acf?.show ?? true
        commit('SET_COMMISSION_SETTINGS', Boolean(show))
      } catch (e) {
        console.error('[store] fetchCommissionSettings 失敗，預設開放', e)
        commit('SET_COMMISSION_SETTINGS', true)
      }
    },

    resetBlog({ commit }) {
      commit('RESET_BLOG')
    },

    async fetchBlogTerms({ commit, state }, taxonomy) {
      if (state.blogTerms[taxonomy]) return state.blogTerms[taxonomy]
      try {
        const { data } = await wp.get(`/${taxonomy}`, { params: { per_page: 100 } })
        const terms = data
          .filter(t => (t.count_by_type?.post ?? t.count ?? 0) > 0)
          .map(t => ({ id: t.id, name: decodeHtml(t.name), slug: t.slug, count: t.count_by_type?.post ?? t.count ?? 0 }))
        commit('SET_BLOG_TERMS', { taxonomy, terms })
        return terms
      } catch (e) {
        console.error(`[store] fetchBlogTerms(${taxonomy}) 失敗`, e)
        return []
      }
    },

    async fetchBlogPost({ commit, state }, slug) {
      if (state.blogPostCache[slug]) return state.blogPostCache[slug]
      try {
        const { data } = await wp.get('/posts', { params: { slug } })
        const p = data[0]
        if (!p) return null
        const [thumbnail, blocks] = await Promise.all([
          fetchMediaUrl(p.featured_media),
          parseContentBlocks(p.acf?.content_block, p.acf?.content),
        ])
        const terms = state.blogTerms
        const post = {
          id:         p.id,
          title:      decodeHtml(p.title?.rendered ?? ''),
          slug:       p.slug,
          date:       p.date?.slice(0, 10) ?? '',
          excerpt:    acfExcerpt(p.acf?.excerpt) ?? p.excerpt?.rendered ?? '',
          thumbnail,
          categories: mapTermIds(p.categories ?? [], terms.categories ?? []),
          tags:       mapTermIds(p.tags       ?? [], terms.tags       ?? []),
          skills:     mapTermIds(p.skills     ?? [], terms.skills     ?? []),
          interests:  mapTermIds(p.interests  ?? [], terms.interests  ?? []),
          blocks,
        }
        commit('SET_BLOG_POST_CACHE', post)
        return post
      } catch (e) {
        console.error('[store] fetchBlogPost 失敗', e)
        return null
      }
    },

    async fetchBlogPage({ commit, state }, { search = '', filters = {}, perPage = 10 } = {}) {
      if (state.blogLoading || !state.blogHasMore) return
      commit('SET_BLOG_LOADING', true)
      try {
        const params = { per_page: perPage, page: state.blogPage, search }
        if (filters.categories?.length) params.categories = filters.categories.join(',')
        if (filters.tags?.length)       params.tags        = filters.tags.join(',')
        if (filters.skills?.length)     params.skills      = filters.skills.join(',')
        if (filters.interests?.length)  params.interests   = filters.interests.join(',')

        const { data, headers } = await wp.get('/posts', { params })
        const totalPages = parseInt(headers['x-wp-totalpages'] ?? '1', 10)
        const terms = state.blogTerms

        const posts = await Promise.all(data.map(async p => ({
          id:         p.id,
          title:      decodeHtml(p.title?.rendered ?? ''),
          slug:       p.slug,
          date:       p.date?.slice(0, 10) ?? '',
          excerpt:    acfExcerpt(p.acf?.excerpt) ?? p.excerpt?.rendered ?? '',
          thumbnail:  await fetchMediaUrl(p.featured_media),
          categories: mapTermIds(p.categories ?? [], terms.categories ?? []),
          tags:       mapTermIds(p.tags       ?? [], terms.tags       ?? []),
          skills:     mapTermIds(p.skills     ?? [], terms.skills     ?? []),
          interests:  mapTermIds(p.interests  ?? [], terms.interests  ?? []),
        })))

        const nextPage = state.blogPage + 1
        commit('APPEND_BLOG_POSTS', posts)
        commit('SET_BLOG_PAGE', nextPage)
        commit('SET_BLOG_HAS_MORE', nextPage <= totalPages)
      } catch (e) {
        console.error('[store] fetchBlogPage 失敗', e)
        commit('SET_BLOG_HAS_MORE', false)
      } finally {
        commit('SET_BLOG_LOADING', false)
      }
    },
  },
})
